import { z } from "zod";
import { CALL_DIRECTIONS } from "./speech_plan.zod.js";
/**
 * What a call cost, and the rates that decide it.
 *
 * Two surfaces share this file because they are two halves of one loop: the
 * calculator shows what traffic cost and names every line it could not value;
 * the rate editor is where those gaps get filled. A number missing from one is
 * a row missing from the other.
 *
 * ## Quantities are stored; money is computed
 *
 * Nothing here is a stamped amount. A call's cost is its stored quantities
 * priced at the rates **valid on the call's date**, which is what lets a rate
 * entered today for last month's traffic change last month's figure — the
 * whole point of dated rates. Reproducibility comes from the rates being
 * append-only versions, not from freezing the answer: `asOf` re-runs any
 * figure with the rates as they were known at that moment.
 */
/**
 * Every service a cost can land on.
 *
 * `telephony` is the carrier, `storage` is recordings. The runtime reports
 * `stt`, `llm` and `tts` itself; the other two are read from the tables that
 * already hold them (`telephony_calls`, `call_recordings`).
 */
export const COST_SERVICES = ["telephony", "stt", "llm", "tts", "storage"];
/** The legs the runtime can attribute usage to. Mirrors `VoicelyRealtime.Usage.kinds/0`. */
export const RUNTIME_COST_KINDS = ["llm", "stt", "telephony", "tts"];
/** Every unit the runtime emits. Mirrors `VoicelyRealtime.Usage.units/0`. */
export const RUNTIME_COST_UNITS = [
    "audio_seconds",
    "cached_input_tokens",
    "characters",
    "input_tokens",
    "output_tokens",
    "requests",
    "stream_seconds",
];
/**
 * Every unit a rate can be written against.
 *
 * The runtime's units, plus what the carrier and the bucket report:
 * `talk_seconds` (the carrier's Billsec, exact), `ring_seconds`, `attempts`
 * (one per placed call, answered or not), `billed_seconds` (the carrier's own
 * pulse-rounded figure — kept to check our pulse maths, never priced), and
 * the recording's size and length.
 */
export const COST_UNITS = [
    ...RUNTIME_COST_UNITS,
    "attempts",
    "billed_seconds",
    "recording_bytes",
    "recording_seconds",
    "ring_seconds",
    "talk_seconds",
];
/** Units measured in seconds — the only ones a per-minute pulse can apply to. */
export const SECONDS_UNITS = [
    "audio_seconds",
    "billed_seconds",
    "recording_seconds",
    "ring_seconds",
    "stream_seconds",
    "talk_seconds",
];
/**
 * Why a line of usage was spent.
 *
 * Post-call summary and extraction run on the same model the conversation did,
 * so without this the two share a row and neither can be priced or filtered
 * on its own.
 */
export const COST_PURPOSES = ["conversation", "post_call"];
/** Where a rate was set, most specific first. */
export const RATE_LEVELS = ["model", "vendor", "gateway", "service"];
/**
 * Where a costed call ran: the two telephony directions, plus the browser.
 *
 * Derived from the runtime's own `CALL_DIRECTIONS` rather than declared
 * again: a costing report and a grant must agree on what a direction is
 * called, and two lists that happened to overlap used to be the only thing
 * keeping them in step. `web` is costing's addition — a browser session is
 * `outbound` to the runtime (it picks the outbound opening) but is priced
 * without a carrier leg, which is the difference a cost report groups by.
 */
export const COSTED_CALL_DIRECTIONS = [...CALL_DIRECTIONS, "web"];
/** ISO-4217, upper-case. */
const currencyZod = z
    .string()
    .length(3)
    .regex(/^[A-Z]{3}$/, "ISO-4217, upper-case");
/** A calendar day in the platform's reporting timezone (Asia/Kolkata), `YYYY-MM-DD`. */
export const costDateZod = z.iso.date();
/** Millionths of one unit of the rate's currency. Integers, because a float loses money at scale. */
const microsZod = z.number().int().min(0).max(1_000_000_000_000);
// ── Pricing shapes ─────────────────────────────────────────────────────────
/**
 * How one rate turns a quantity into money.
 *
 * Every shape a vendor has been seen to bill in reduces to one of these. They
 * are closed on purpose: a shape the rating engine does not know would be
 * silently priced as something else.
 *
 * - `per_unit` — `quantity ÷ per × price`. Per character, per 1M tokens, per
 *   60 seconds.
 * - `pulse` — per minute, rounded up to a pulse after a first pulse and a
 *   minimum, applied to the whole call or to each event. Telephony, and STT
 *   billed in blocks per utterance.
 * - `per_event_minimum` — linear, but no event is billed below a floor. A TTS
 *   vendor with a minimum per request.
 * - `context_tiered` — the price of a request depends on its size. An LLM
 *   priced higher above a context length.
 * - `flat_per_call` — once per call that used the meter at all. A per-attempt
 *   fee.
 */
export const pricingZod = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("per_unit"),
        priceMicros: microsZod,
        /** How many units `priceMicros` buys: 1 per character, 1_000_000 per token-million, 60 per second-minute. */
        per: z.number().positive(),
    }),
    z.object({
        type: z.literal("pulse"),
        pricePerMinuteMicros: microsZod,
        pulseSeconds: z.number().positive(),
        /** The first billed block. Defaults to `pulseSeconds` — "60/60" and "30/15" billing differ exactly here. */
        firstPulseSeconds: z.number().positive().optional(),
        /** Anything shorter than this, that is not zero, is billed as this. */
        minimumSeconds: z.number().min(0).optional(),
        /** `call` rounds the call's total; `segment` rounds every event (utterance) on its own. */
        appliesTo: z.enum(["call", "segment"]),
    }),
    z.object({
        type: z.literal("per_event_minimum"),
        priceMicros: microsZod,
        per: z.number().positive(),
        minimumPerEvent: z.number().min(0),
    }),
    z.object({
        type: z.literal("context_tiered"),
        /** Ordered by `upToPerRequest`; the last band's null means "and above". */
        bands: z
            .array(z.object({
            upToPerRequest: z.number().positive().nullable(),
            priceMicros: microsZod,
            per: z.number().positive(),
        }))
            .min(1)
            .max(10),
    }),
    z.object({
        type: z.literal("flat_per_call"),
        priceMicros: microsZod,
    }),
]);
// ── Rates ──────────────────────────────────────────────────────────────────
/**
 * Where a rate sits: `service → gateway → vendor → model`, with null meaning
 * "inherit from above". `gateway` is who bills us when it is not the vendor —
 * LiveKit reselling Deepgram — and `""` means the vendor is billed directly.
 * `model` of `""` is the lane that pinned none, served by the adapter's own
 * default, which is a real binding production emits.
 */
const rateScopeZod = z.object({
    service: z.enum(COST_SERVICES),
    gateway: z.string().max(64).nullable(),
    vendor: z.string().max(64).nullable(),
    model: z.string().max(128).nullable(),
    /** Null for every purpose. Set only when post-call inference is priced differently. */
    purpose: z.enum(COST_PURPOSES).nullable(),
    unit: z.enum(COST_UNITS),
});
/**
 * Writing a rate.
 *
 * Always a NEW version. `supersedes` names the version it replaces, which is
 * then closed rather than edited — the old number stays on the record so any
 * figure it produced can be reproduced.
 *
 * `source` is required for anything non-zero, the one piece of review
 * discipline a form can enforce: a rate nobody can trace reads exactly like
 * one that was checked.
 */
export const rateInputZod = rateScopeZod
    .extend({
    pricing: pricingZod,
    currency: currencyZod,
    validFrom: costDateZod,
    /** Exclusive. Null means open-ended. */
    validTo: costDateZod.nullable(),
    source: z.string().max(500).optional(),
    note: z.string().max(1000).optional(),
    supersedes: z.ulid().optional(),
})
    .superRefine((rate, ctx) => {
    if (rate.model !== null && rate.vendor === null) {
        ctx.addIssue({
            code: "custom",
            path: ["vendor"],
            message: "A model-level rate needs its vendor — a model name alone does not say whose it is.",
        });
    }
    if (rate.pricing.type === "pulse" && !SECONDS_UNITS.includes(rate.unit)) {
        ctx.addIssue({
            code: "custom",
            path: ["pricing"],
            message: "A per-minute pulse only means something on a unit measured in seconds.",
        });
    }
    if (rate.validTo !== null && rate.validTo <= rate.validFrom) {
        ctx.addIssue({
            code: "custom",
            path: ["validTo"],
            message: "validTo must be after validFrom.",
        });
    }
});
export const rateZod = rateScopeZod.extend({
    id: z.string(),
    pricing: pricingZod,
    currency: z.string(),
    validFrom: z.string(),
    validTo: z.string().nullable(),
    source: z.string().nullable(),
    note: z.string().nullable(),
    createdByEmail: z.string().nullable(),
    createdAt: z.number(),
    supersededAt: z.number().nullable(),
    supersededByEmail: z.string().nullable(),
    level: z.enum(RATE_LEVELS),
});
// ── Account-level charges ──────────────────────────────────────────────────
/**
 * Volume slabs: a vendor's price per unit falling as the month's volume
 * rises.
 *
 * **Never allocated to calls.** A slab is a property of the whole account's
 * month, so a call's cost is always its list rate and the slab shows beside
 * the total as an adjustment — computed from platform-wide volume, because
 * that is what the vendor counts.
 */
export const slabInputZod = z
    .object({
    service: z.enum(COST_SERVICES),
    gateway: z.string().max(64).nullable(),
    vendor: z.string().max(64).nullable(),
    model: z.string().max(128).nullable(),
    unit: z.enum(COST_UNITS),
    tiers: z
        .array(z.object({
        /** Cumulative monthly volume this tier runs up to; null on the last tier. */
        upTo: z.number().positive().nullable(),
        priceMicros: microsZod,
        per: z.number().positive(),
    }))
        .min(1)
        .max(20),
    currency: currencyZod,
    validFrom: costDateZod,
    validTo: costDateZod.nullable(),
    source: z.string().max(500).optional(),
    note: z.string().max(1000).optional(),
    supersedes: z.ulid().optional(),
})
    .superRefine((slab, ctx) => {
    const last = slab.tiers.length - 1;
    slab.tiers.forEach((tier, index) => {
        if ((tier.upTo === null) !== (index === last)) {
            ctx.addIssue({
                code: "custom",
                path: ["tiers", index, "upTo"],
                message: "Only the last tier is open-ended, and it must be.",
            });
        }
        const previous = slab.tiers[index - 1]?.upTo;
        if (index > 0 && tier.upTo !== null && previous != null && tier.upTo <= previous) {
            ctx.addIssue({
                code: "custom",
                path: ["tiers", index, "upTo"],
                message: "Tiers must increase.",
            });
        }
    });
});
export const slabZod = z.object({
    id: z.string(),
    service: z.enum(COST_SERVICES),
    gateway: z.string().nullable(),
    vendor: z.string().nullable(),
    model: z.string().nullable(),
    unit: z.enum(COST_UNITS),
    tiers: z.array(z.object({ upTo: z.number().nullable(), priceMicros: z.number(), per: z.number() })),
    currency: z.string(),
    validFrom: z.string(),
    validTo: z.string().nullable(),
    source: z.string().nullable(),
    note: z.string().nullable(),
    createdByEmail: z.string().nullable(),
    createdAt: z.number(),
    supersededAt: z.number().nullable(),
});
/** What one month of a fixed fee is multiplied by. */
export const FEE_BASES = ["flat", "per_phone_number", "per_channel"];
/**
 * A monthly charge that no call causes: number rental, a channel fee, a
 * platform subscription. Shown beside the total, never inside a call.
 */
export const fixedFeeInputZod = z.object({
    service: z.enum(COST_SERVICES),
    vendor: z.string().max(64).nullable(),
    description: z.string().min(1).max(200),
    basis: z.enum(FEE_BASES),
    /** Per month, per whatever `basis` counts. */
    amountMicros: microsZod,
    currency: currencyZod,
    validFrom: costDateZod,
    validTo: costDateZod.nullable(),
    source: z.string().max(500).optional(),
    note: z.string().max(1000).optional(),
    supersedes: z.ulid().optional(),
});
export const fixedFeeZod = z.object({
    id: z.string(),
    service: z.enum(COST_SERVICES),
    vendor: z.string().nullable(),
    description: z.string(),
    basis: z.enum(FEE_BASES),
    amountMicros: z.number(),
    currency: z.string(),
    validFrom: z.string(),
    validTo: z.string().nullable(),
    source: z.string().nullable(),
    note: z.string().nullable(),
    createdByEmail: z.string().nullable(),
    createdAt: z.number(),
    supersededAt: z.number().nullable(),
});
// ── Currency ───────────────────────────────────────────────────────────────
/**
 * What one unit of a vendor's currency cost in the base currency, over a
 * period.
 *
 * `markupBps` is what the bank or card adds on top of the market rate — the
 * difference between a cost that matches the vendor's invoice and one that
 * matches the bank statement. 350 is 3.5%.
 */
export const fxRateInputZod = z.object({
    currency: currencyZod,
    /** Micros of the base currency per one whole unit of `currency`. USD→INR at ₹88.50 is 88_500_000. */
    microsPerUnit: z.number().int().min(1).max(1_000_000_000_000),
    markupBps: z.number().int().min(0).max(10_000).default(0),
    validFrom: costDateZod,
    validTo: costDateZod.nullable(),
    source: z.string().max(500).optional(),
    supersedes: z.ulid().optional(),
});
export const fxRateZod = z.object({
    id: z.string(),
    currency: z.string(),
    microsPerUnit: z.number(),
    markupBps: z.number().int(),
    validFrom: z.string(),
    validTo: z.string().nullable(),
    source: z.string().nullable(),
    updatedByEmail: z.string().nullable(),
    createdAt: z.number(),
    supersededAt: z.number().nullable(),
});
// ── Writing the book ───────────────────────────────────────────────────────
/** The four kinds of version the book holds. */
export const BOOK_KINDS = ["rate", "slab", "fixed_fee", "fx_rate"];
/**
 * One write to the book: a new version of any of its four kinds.
 *
 * One procedure rather than four because each procedure's type carries the
 * whole authenticated context with it, and eight of them made the app
 * router's type too large for the compiler to serialise.
 */
export const createVersionInputZod = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("rate"), rate: rateInputZod }),
    z.object({ kind: z.literal("slab"), slab: slabInputZod }),
    z.object({ kind: z.literal("fixed_fee"), fee: fixedFeeInputZod }),
    z.object({ kind: z.literal("fx_rate"), fx: fxRateInputZod }),
]);
/** Closes a version of any kind with no successor. */
export const retireVersionInputZod = z.object({ kind: z.enum(BOOK_KINDS), id: z.ulid() });
// ── The calculator ─────────────────────────────────────────────────────────
export const CALL_OUTCOMES = ["all", "connected", "unanswered"];
export const COST_GROUP_BYS = [
    "none",
    "team",
    "agent",
    "batch",
    "day",
    "direction",
    "service",
    "vendor",
    "model",
    "purpose",
];
/**
 * Which calls, and which of their costs.
 *
 * Call filters (team, agent, batch, direction, outcome, dates) choose calls.
 * Line filters (service, vendor, model, purpose) choose which of those calls'
 * costs are counted — "what did STT cost on these calls" — and never drop a
 * call, so connected minutes stay the minutes of the calls chosen.
 */
export const costFiltersZod = z
    .object({
    from: costDateZod,
    /** Inclusive. */
    to: costDateZod,
    teamIds: z.array(z.ulid()).max(50).default([]),
    agentIds: z.array(z.ulid()).max(50).default([]),
    batchId: z.ulid().optional(),
    direction: z.enum(COSTED_CALL_DIRECTIONS).optional(),
    outcome: z.enum(CALL_OUTCOMES).default("all"),
    service: z.enum(COST_SERVICES).optional(),
    vendor: z.string().max(64).optional(),
    model: z.string().max(128).optional(),
    purpose: z.enum(COST_PURPOSES).optional(),
})
    .refine((filters) => filters.from <= filters.to, {
    path: ["to"],
    message: "to must not be before from.",
});
/**
 * What the customer is charged for a connected call: a price per minute,
 * billed in pulses of connected (talk) time. Unanswered attempts are free to
 * the customer, so they earn nothing and still cost what they cost.
 *
 * Not saved anywhere — the calculator answers "at this price, on these
 * calls, do we make money", and the page keeps the question in its URL.
 */
export const sellingPriceZod = z.object({
    pricePerMinuteMicros: microsZod,
    pulseSeconds: z.number().int().min(1).max(60),
    /** The first billed block, as in "30/15". Defaults to `pulseSeconds`. */
    firstPulseSeconds: z.number().int().min(1).max(300).optional(),
});
export const costCalculatorInputZod = z.object({
    filters: costFiltersZod,
    selling: sellingPriceZod.optional(),
    groupBy: z.enum(COST_GROUP_BYS).default("none"),
    /**
     * Re-run with the rates as they were known at this instant (epoch ms).
     * How a figure somebody already quoted is reproduced after rates changed.
     */
    asOf: z.number().int().optional(),
    /**
     * Unsaved rates to try. Each beats a saved rate at the same level — the
     * what-if a rate editor exists for, without writing anything.
     */
    draftRates: z.array(rateInputZod).max(200).default([]),
    callsLimit: z.number().int().min(1).max(1000).default(100),
    callsOffset: z.number().int().min(0).default(0),
});
/** Money by service, in base-currency micros. */
const byServiceZod = z.record(z.string(), z.number());
/**
 * Money by why it was spent, in base-currency micros.
 *
 * A second cut through the same lines, not a second set of them: post-call
 * summary and extraction are `llm` spend, so they are already inside
 * `byService.llm`. Split out because they are the one part of a call's cost
 * that scales with transcript length rather than with minutes, and folded into
 * "Inference" they cannot be told apart from the conversation.
 */
const byPurposeZod = z.record(z.string(), z.number());
/**
 * One line of one call: a quantity, and what it cost.
 *
 * `amountMicros` is base currency and null when the line could not be valued —
 * no rate matched, or its currency had no FX rate on that day. **Null is not
 * zero**, and the quantity stays either way.
 */
export const costLineZod = z.object({
    service: z.enum(COST_SERVICES),
    gateway: z.string(),
    vendor: z.string(),
    model: z.string(),
    purpose: z.enum(COST_PURPOSES),
    unit: z.string(),
    /** `runtime` and `carrier` are priced; `vendor` is the vendor's own figure, kept to compare. */
    source: z.string(),
    quantity: z.number(),
    events: z.number().int().nullable(),
    amountMicros: z.number().nullable(),
    /** In the rate's own currency, before conversion — how a converted figure is traced back. */
    rateAmountMicros: z.number().nullable(),
    currency: z.string().nullable(),
    rateId: z.string().nullable(),
    level: z.enum(RATE_LEVELS).nullable(),
    /** True when a per-event rate had to cost the total because the events were incomplete. */
    costedFromTotal: z.boolean(),
    /** Why the line has no amount, when it has none. */
    gap: z.enum(["unpriced", "unconverted"]).nullable(),
});
export const callCostZod = z.object({
    sessionId: z.string(),
    teamId: z.string().nullable(),
    teamName: z.string().nullable(),
    agentId: z.string().nullable(),
    agentName: z.string().nullable(),
    batchId: z.string().nullable(),
    direction: z.enum(COSTED_CALL_DIRECTIONS),
    sessionStatus: z.string(),
    connected: z.boolean(),
    startedAt: z.number().nullable(),
    /** Carrier talk time, or the session's own duration for a browser call — see `connectedSource`. */
    connectedSeconds: z.number(),
    /**
     * `carrier`: a figure the carrier reported. `clocks`: it reported none, so
     * its answer→hangup timestamps. `session`: no carrier leg (browser).
     */
    connectedSource: z.enum(["carrier", "clocks", "session", "none"]),
    totalMicros: z.number(),
    byService: byServiceZod,
    byPurpose: byPurposeZod,
    /** Null when the call has no connected time to divide by. */
    costPerConnectedMinuteMicros: z.number().nullable(),
    /** True when any counted line could not be valued, so `totalMicros` is a floor. */
    partial: z.boolean(),
    /** What the carrier said it charged, converted — for reconciliation. Null when it said nothing. */
    carrierCostMicros: z.number().nullable(),
    /** What the customer is charged at the selling price, when one was given. */
    revenueMicros: z.number().nullable().optional(),
    lines: z.array(costLineZod),
});
export const costGroupZod = z.object({
    key: z.string(),
    label: z.string(),
    calls: z.number().int(),
    connectedCalls: z.number().int(),
    connectedSeconds: z.number(),
    totalMicros: z.number(),
    byService: byServiceZod,
    byPurpose: byPurposeZod,
    costPerConnectedMinuteMicros: z.number().nullable(),
    avgPerCallMicros: z.number().nullable(),
    /**
     * At the selling price, when one was given. Null too when grouping by a
     * line property (service, vendor…): a customer pays for a call, not for
     * its synthesis.
     */
    revenueMicros: z.number().nullable(),
    marginMicros: z.number().nullable(),
});
/** One priced binding: the scope a rate is written against, down to the unit. */
export const costBindingZod = z.object({
    service: z.enum(COST_SERVICES),
    gateway: z.string(),
    vendor: z.string(),
    model: z.string(),
    purpose: z.enum(COST_PURPOSES),
    /** A string, like `costLineZod.unit`: a ledger row can carry a unit no rate is allowed to name yet. */
    unit: z.string(),
});
/** What one binding cost across the window. The row a wrong rate shows up on first. */
export const costBreakdownRowZod = z.object({
    key: z.string(),
    service: z.enum(COST_SERVICES),
    gateway: z.string(),
    vendor: z.string(),
    model: z.string(),
    purpose: z.enum(COST_PURPOSES),
    unit: z.string(),
    quantity: z.number(),
    amountMicros: z.number(),
    calls: z.number().int(),
    rateIds: z.array(z.string()),
    levels: z.array(z.enum(RATE_LEVELS)),
    unpricedLines: z.number().int(),
    unconvertedLines: z.number().int(),
    costedFromTotalLines: z.number().int(),
});
export const overheadLineZod = z.object({
    kind: z.enum(["fixed_fee", "slab_adjustment"]),
    id: z.string(),
    label: z.string(),
    service: z.enum(COST_SERVICES),
    month: z.string(),
    /** Fraction of the month the window covers; overheads are prorated by days. */
    share: z.number(),
    quantity: z.number().nullable(),
    amountMicros: z.number().nullable(),
    note: z.string().nullable(),
});
export const costCalculationZod = z.object({
    baseCurrency: z.string(),
    timezone: z.string(),
    from: z.string(),
    to: z.string(),
    asOf: z.number().nullable(),
    draftRatesApplied: z.number().int(),
    /**
     * True when the window held more calls than one calculation reads, so every
     * figure is computed from a prefix of the real data. Reported, never
     * swallowed — a truncated total is lower than the truth with nothing saying
     * so.
     */
    truncated: z.boolean(),
    callCap: z.number().int(),
    totals: z.object({
        calls: z.number().int(),
        connectedCalls: z.number().int(),
        unansweredCalls: z.number().int(),
        connectedSeconds: z.number(),
        /** How many calls' minutes came from the session clock rather than the carrier — browser calls. */
        sessionClockCalls: z.number().int(),
        carrierClockCalls: z.number().int(),
        totalMicros: z.number(),
        byService: byServiceZod,
        byPurpose: byPurposeZod,
        unansweredCostMicros: z.number(),
        avgPerCallMicros: z.number().nullable(),
        avgPerConnectedCallMicros: z.number().nullable(),
        costPerConnectedMinuteMicros: z.number().nullable(),
        partialCalls: z.number().int(),
    }),
    groups: z.array(costGroupZod),
    breakdown: z.array(costBreakdownRowZod),
    calls: z.array(callCostZod),
    gaps: z.object({
        /** `service:gateway/vendor/model:unit` keys traffic hit that nothing prices — the worklist. */
        unpricedKeys: z.array(z.string()),
        /**
         * The same worklist as scopes, one per key, so a missing rate can be
         * opened in the editor already filled in rather than retyped from a chip.
         */
        unpriced: z.array(costBindingZod),
        unconvertedCurrencies: z.array(z.string()),
        /** Keys priced by a rate set above their own level, e.g. a new vendor riding the service default. */
        inheritedKeys: z.array(z.string()),
        costedFromTotalKeys: z.array(z.string()),
    }),
    /**
     * Revenue and margin at the selling price, over the same calls. Null when
     * no selling price was given. Margin is call revenue less call cost —
     * overheads are platform-wide and reported apart, never inside a call.
     */
    selling: z
        .object({
        pricePerMinuteMicros: z.number(),
        pulseSeconds: z.number(),
        firstPulseSeconds: z.number(),
        /** Connected seconds rounded up to the selling pulse — what the customer is billed for. */
        billedSeconds: z.number(),
        revenueMicros: z.number(),
        costMicros: z.number(),
        marginMicros: z.number(),
        /** Margin over revenue, in basis points. Null with no revenue. */
        marginBps: z.number().nullable(),
        revenuePerConnectedMinuteMicros: z.number().nullable(),
    })
        .nullable(),
    /**
     * The same calls billed at each candidate customer pulse: how many seconds
     * the customer would be billed, and the per-minute price at which revenue
     * exactly covers call cost. Shorter pulses bill fewer seconds, so need a
     * higher price to break even — this is that trade, measured on real call
     * lengths. Revenue and margin are filled in when a selling price is given.
     */
    pulseOptions: z.array(z.object({
        pulseSeconds: z.number(),
        billedSeconds: z.number(),
        breakEvenPricePerMinuteMicros: z.number().nullable(),
        revenueMicros: z.number().nullable(),
        marginMicros: z.number().nullable(),
    })),
    /**
     * What the carrier's pulse does to telephony, over the same calls.
     *
     * The carrier bills whole pulses (vobiz: 60/60) while connected minutes are
     * exact, so short calls pay for time nobody talked. `talkSeconds` is what
     * was said; `carrierBilledSeconds` is the carrier's own pulse-rounded
     * figure where it sent one — evidence beside our pricing, not the price.
     * Their ratio is the overhead the pulse adds; the length histogram says
     * which calls cause it.
     */
    telephony: z.object({
        /** Connected calls carrying a talk-time line. */
        calls: z.number().int(),
        talkSeconds: z.number(),
        /** Calls where the carrier sent a billed duration, and its sum over them. */
        carrierBilledCalls: z.number().int(),
        carrierBilledSeconds: z.number(),
        /** Talk seconds of just the calls in `carrierBilledCalls`, so the ratio compares like with like. */
        talkSecondsOfCarrierBilled: z.number(),
        amountMicros: z.number(),
        perTalkMinuteMicros: z.number().nullable(),
        /** Talk-length buckets: `upToSeconds` inclusive, null for the open-ended last one. */
        lengths: z.array(z.object({
            upToSeconds: z.number().nullable(),
            calls: z.number().int(),
            talkSeconds: z.number(),
            amountMicros: z.number(),
        })),
    }),
    reconciliation: z.object({
        /** Calls where the carrier reported its own charge. */
        calls: z.number().int(),
        ourTelephonyMicros: z.number(),
        carrierMicros: z.number(),
        unconvertedCarrierCurrencies: z.array(z.string()),
    }),
    /** Fixed fees and slab adjustments: platform-wide, prorated by days, never inside a call. */
    overheads: z.object({
        lines: z.array(overheadLineZod),
        totalMicros: z.number(),
    }),
});
/** A voice agent, for the calculator's filter. */
export const costAgentZod = z.object({
    id: z.string(),
    name: z.string(),
    teamId: z.string().nullable(),
});
/** A batch, for the calculator's filter. */
export const costBatchZod = z.object({
    id: z.string(),
    name: z.string(),
    teamId: z.string(),
});
/** The units a post-call request is metered in — see `writePostCallUsage`. */
export const POST_CALL_UNITS = [
    "input_tokens",
    "cached_input_tokens",
    "output_tokens",
    "requests",
];
/**
 * One model post-call analysis runs on, and what the rate book says about it
 * today.
 *
 * Derived from what agents are CONFIGURED with, not from traffic, which is the
 * point: the calculator's worklist names a binding only after a call has
 * already been costed without a rate, and a newly-picked extraction model
 * should be priceable before its first call rather than after.
 */
export const postCallBindingZod = z.object({
    provider: z.string(),
    gateway: z.string(),
    vendor: z.string(),
    model: z.string(),
    /** `summary` runs on the agent's own LLM; `extraction` on the model chosen for it. */
    roles: z.array(z.enum(["summary", "extraction"])),
    /** Agents using it in either role. Zero for an offered cold-path model nobody has picked yet. */
    agents: z.number().int(),
    units: z.array(z.object({
        unit: z.enum(POST_CALL_UNITS),
        /** Null when nothing in the book prices this unit for post-call today. */
        level: z.enum(RATE_LEVELS).nullable(),
        rateId: z.string().nullable(),
        /** True when the rate that answers is scoped to `post_call` rather than shared with the conversation. */
        postCallOnly: z.boolean(),
    })),
});
//# sourceMappingURL=costing.zod.js.map